import { useState } from 'react'
import { PrimaryFilter } from './campaign-configuration/PrimaryFilter'
import {
   Button,
   ContentCard,
   Grid,
   Group,
   Radio,
   Stack,
} from '@forwardflow/ui-v2'
import { ArrowLeftMdIcon, ChevronRightIcon } from '@forwardflow/icons'
import { useOnboardingStoreV2 } from '@forwardflow/store'
import { FilterConfirmation } from './campaign-configuration/FilterConfirmation'
import { PRIMARY_FILTER_OPTIONS } from '@forwardflow/constants/onboarding.constants'
import { FilterRow } from './campaign-configuration/FilterRow'
import { IFilterCriteria } from '@forwardflow/types/onboarding'
import { KindsOfLeads } from './campaign-configuration/KindsOfLeads'
import FilterDefinition from './campaign-configuration/FilterDefinition'

export const CampaignConfiguration = () => {
   const [step, setStep] = useState<number>(1)
   const {
      getFirstFilterCriteria,
      getRestFilterCriterias,
      setCampaignConfiguration,
   } = useOnboardingStoreV2()
   const firstFilterCriteria = getFirstFilterCriteria()
   const existingFilterCriterias = getRestFilterCriterias()

   const [selectedFilterId, setSelectedFilterId] = useState<string | null>(
      firstFilterCriteria?.field || null
   )
   const [shouldAddMoreFilters, setShouldAddMoreFilters] = useState<boolean>(
      existingFilterCriterias.length > 0
   )

   const [filterCriterias, setFilterCriterias] = useState<IFilterCriteria[]>(
      existingFilterCriterias.length > 0
         ? (existingFilterCriterias as IFilterCriteria[])
         : []
   )

   // Get fields that are already used (including the primary filter)
   const getUsedFields = () => {
      const usedFields = new Set<string>()
      if (firstFilterCriteria?.field) {
         usedFields.add(firstFilterCriteria.field)
      }
      filterCriterias.forEach((criteria) => {
         if (criteria.field) {
            usedFields.add(criteria.field)
         }
      })
      return usedFields
   }

   // Get available fields for a new filter
   const getAvailableFields = () => {
      const usedFields = getUsedFields()
      return PRIMARY_FILTER_OPTIONS.filter(
         (option) => !usedFields.has(option.value)
      )
   }

   // Add a new filter
   const handleAddFilter = () => {
      const availableFields = getAvailableFields()
      if (availableFields.length === 0) return

      const newFilter: IFilterCriteria = {
         field: availableFields[0].value as
            | 'source'
            | 'stage'
            | 'tags'
            | 'assigned_pond_id',
         value: [],
         logic: 'AND',
         operator: 'CONTAINS_ANY',
      }
      setFilterCriterias([...filterCriterias, newFilter])
   }

   // Remove a filter
   const handleRemoveFilter = (index: number) => {
      setFilterCriterias(filterCriterias.filter((_, i) => i !== index))
   }

   // Update a filter
   const handleUpdateFilter = (index: number, filter: IFilterCriteria) => {
      const updatedFilters = [...filterCriterias]
      updatedFilters[index] = filter
      setFilterCriterias(updatedFilters)
   }

   // Handle adding more filters toggle
   const handleAddMoreFiltersToggle = (value: boolean) => {
      setShouldAddMoreFilters(value)
      if (value && filterCriterias.length === 0) {
         // Add a default filter when toggling to "Yes"
         const availableFields = getAvailableFields()
         if (availableFields.length > 0) {
            const newFilter: IFilterCriteria = {
               field: availableFields[0].value as
                  | 'source'
                  | 'stage'
                  | 'tags'
                  | 'assigned_pond_id',
               value: [],
               logic: 'AND',
               operator: 'CONTAINS_ANY',
            }
            setFilterCriterias([newFilter])
         }
      }
   }

   // Save filters and continue
   const handleContinue = () => {
      // Only save valid filters (with selected values)
      const validFilters = filterCriterias.filter(
         (criteria) => criteria.value.length > 0
      )

      if (shouldAddMoreFilters && validFilters.length > 0) {
         setCampaignConfiguration({
            campaign_filters: {
               eligible_criteria: [
                  firstFilterCriteria as IFilterCriteria,
                  ...validFilters,
               ],
            },
         })
      } else {
         // If no additional filters, just keep the primary filter
         setCampaignConfiguration({
            campaign_filters: {
               eligible_criteria: [firstFilterCriteria as IFilterCriteria],
            },
         })
      }
      setStep(4)
   }

   return (
      <Stack gap={32}>
         {step === 1 && (
            <PrimaryFilter
               setStep={setStep}
               selectedPrimaryFilter={selectedFilterId}
               setSelectedPrimaryFilter={setSelectedFilterId}
            />
         )}
         {step === 2 && (
            <FilterConfirmation
               setStep={setStep}
               selectedFilterId={selectedFilterId as string}
            />
         )}
         {step === 3 && (
            <>
               <ContentCard className="!p-0 space-y-4">
                  <Stack className="p-6 border-b border-[#272E35]">
                     <p className="text-white font-semibold text-lg">
                        Which leads do you want Forward Flow to work on first?
                     </p>
                     <FilterDefinition
                        filterCriteria={firstFilterCriteria as IFilterCriteria}
                     />
                  </Stack>
                  <Stack className="p-6 pt-0">
                     <Stack gap={0}>
                        <p className="text-white font-semibold text-lg">
                           Do you want additional filtering on the leads from
                           your selection?
                        </p>
                        <p className="text-white/70 text-sm">
                           You can add one or more extra layers to narrow down
                           which leads Forward Flow should work on.
                        </p>
                     </Stack>
                     <Group>
                        <Radio
                           checked={shouldAddMoreFilters}
                           onChange={() => handleAddMoreFiltersToggle(true)}
                        />{' '}
                        Yes
                        <Radio
                           checked={!shouldAddMoreFilters}
                           onChange={() => handleAddMoreFiltersToggle(false)}
                        />{' '}
                        No
                     </Group>
                     {shouldAddMoreFilters && (
                        <Stack gap={16}>
                           {filterCriterias.map((filterCriteria, index) => (
                              <FilterRow
                                 key={index}
                                 filterCriteria={filterCriteria}
                                 onUpdate={(filter) =>
                                    handleUpdateFilter(index, filter)
                                 }
                                 onRemove={() => handleRemoveFilter(index)}
                                 usedFields={getUsedFields()}
                              />
                           ))}
                           <Button
                              variant="secondary"
                              className="w-full"
                              onClick={handleAddFilter}
                              disabled={getAvailableFields().length === 0}
                           >
                              Add Filter
                           </Button>
                        </Stack>
                     )}
                  </Stack>
               </ContentCard>
               <Grid>
                  <Grid.Col span={3}>
                     <Button
                        variant="secondary"
                        className="w-full"
                        onClick={() => setStep(2)}
                     >
                        <ArrowLeftMdIcon width={12} /> Back
                     </Button>
                  </Grid.Col>
                  <Grid.Col span={9}>
                     <Button className="w-full" onClick={handleContinue}>
                        Apply Filters <ChevronRightIcon width={18} />
                     </Button>
                  </Grid.Col>
               </Grid>
            </>
         )}
         {step === 4 && <KindsOfLeads setStep={setStep} />}
      </Stack>
   )
}
